/* ═══════════════════════════════════════════════════════════
   FF Trust Strip — Fade Carousel
   ──────────────────────────────────────────────────────────
   Pure fade between items. Activates only when the current
   breakpoint is configured for fade mode (data-desktop="fade"
   or data-mobile="fade"). Re-evaluates on resize so the
   correct behavior applies after a breakpoint crossing.

   Indicators mirror hero slider's rectangular progress-bar
   pattern: width animates 0% → 100% over the autoplay
   interval, pauses with the .is-paused class, snaps to filled
   when autoplay is off via the .no-autoplay class.

   No marquee, no swipe, no directional slide — by design.
   ═══════════════════════════════════════════════════════════ */

class FFTrustStrip extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isPaused = false;
    this.isVisible = true;
    this.mediaQuery = null;
    this.boundOnMediaChange = null;
    this.boundOnVisibilityChange = null;
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.ff-ts__item'));
    this.indicators = Array.from(this.querySelectorAll('.ff-ts__indicator'));

    if (this.items.length < 2) {
      // Single item — nothing to rotate.
      return;
    }

    this.desktopMode = this.dataset.desktop || 'static';
    this.mobileMode = this.dataset.mobile || 'stack';
    this.autoplay = this.dataset.autoplay === 'true';
    this.interval = parseInt(this.dataset.interval, 10) || 4500;
    this.pauseOnHover = this.dataset.pauseHover === 'true';

    // Respect user's motion preferences
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Reflect autoplay-off state via class so CSS can show active indicator as filled
    if (!this.autoplay) {
      this.classList.add('no-autoplay');
    }

    // Wire up indicator clicks (work in any fade mode regardless of autoplay)
    this.setupIndicators();

    // Pause on hover (desktop only — coarse-pointer check inside)
    if (this.pauseOnHover) {
      this.setupHoverPause();
    }

    // Pause when tab is hidden
    this.boundOnVisibilityChange = this.onVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.boundOnVisibilityChange);

    // React to breakpoint crossings between desktop/mobile fade configurations
    this.mediaQuery = window.matchMedia('(min-width: 750px)');
    this.boundOnMediaChange = this.onMediaChange.bind(this);
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.boundOnMediaChange);
    } else {
      this.mediaQuery.addListener(this.boundOnMediaChange);
    }

    // Initial mode evaluation
    this.evaluateMode();
  }

  disconnectedCallback() {
    this.stopAutoplay();

    if (this.boundOnVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundOnVisibilityChange);
    }

    if (this.mediaQuery && this.boundOnMediaChange) {
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.boundOnMediaChange);
      } else {
        this.mediaQuery.removeListener(this.boundOnMediaChange);
      }
    }
  }

  /* ─────────────────────────────────────────────────────────
     Breakpoint evaluation
  ───────────────────────────────────────────────────────── */

  isCurrentlyFade() {
    const isDesktop = this.mediaQuery && this.mediaQuery.matches;
    return isDesktop ? this.desktopMode === 'fade' : this.mobileMode === 'fade';
  }

  evaluateMode() {
    const fadeActive = this.isCurrentlyFade();

    if (fadeActive) {
      // Re-activate to ensure indicator state is in sync for this breakpoint
      this.activate(this.currentIndex, /* withTransition */ false);
      if (this.autoplay && !this.reducedMotion) {
        this.startAutoplay();
      }
    } else {
      this.stopAutoplay();
    }
  }

  onMediaChange() {
    this.evaluateMode();
  }

  /* ─────────────────────────────────────────────────────────
     Activation — sets item visibility, indicator selection,
     and triggers the progress animation when eligible.
  ───────────────────────────────────────────────────────── */

  activate(index, withTransition = true) {
    if (index < 0 || index >= this.items.length) return;

    if (!withTransition) {
      // Suppress fade transition for the initial state to avoid a first-frame flash
      this.items.forEach((item) => { item.style.transition = 'none'; });
    }

    // Item visibility (CSS rules handle the actual opacity in fade mode)
    this.items.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
    });

    // Indicator selection (aria-selected drives the CSS state)
    this.indicators.forEach((ind, i) => {
      ind.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });

    // Clear any in-flight indicator animation so the new active starts fresh
    this._clearIndicatorAnimations();

    // One reflow to commit class/attr changes before restarting the animation
    void this.offsetHeight;

    if (!withTransition) {
      requestAnimationFrame(() => {
        this.items.forEach((item) => { item.style.transition = ''; });
      });
    }

    this.currentIndex = index;

    // Start the progress animation on the new active indicator (if eligible)
    this._startActiveIndicatorIfEligible();
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.items.length;
    this.activate(nextIndex);
  }

  _clearIndicatorAnimations() {
    this.indicators.forEach((ind) => {
      const bar = ind.querySelector('.ff-ts__indicator__progress');
      if (bar) bar.classList.remove('is-animating');
    });
  }

  _startActiveIndicatorIfEligible() {
    if (!this.autoplay) return;
    if (this.reducedMotion) return;
    if (this.isPaused) return;
    if (!this.isVisible) return;
    if (!this.isCurrentlyFade()) return;

    const active = this.indicators.find((ind) => ind.getAttribute('aria-selected') === 'true');
    if (!active) return;
    const bar = active.querySelector('.ff-ts__indicator__progress');
    if (!bar) return;
    bar.classList.add('is-animating');
  }

  /* ─────────────────────────────────────────────────────────
     Autoplay
  ───────────────────────────────────────────────────────── */

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplay || this.reducedMotion) return;
    if (this.isPaused || !this.isVisible) return;

    this.autoplayTimer = window.setInterval(() => {
      if (!this.isPaused && this.isVisible && this.isCurrentlyFade()) {
        this.next();
      }
    }, this.interval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    if (this.autoplay && !this.reducedMotion && this.isCurrentlyFade()) {
      this.startAutoplay();
    }
  }

  /* ─────────────────────────────────────────────────────────
     Hover pause (desktop only — coarse-pointer check inside)
  ───────────────────────────────────────────────────────── */

  setupHoverPause() {
    this.addEventListener('mouseenter', () => {
      if (window.matchMedia('(pointer: coarse)').matches) return;
      this.isPaused = true;
      this.classList.add('is-paused');
      this.stopAutoplay();
    });

    this.addEventListener('mouseleave', () => {
      if (window.matchMedia('(pointer: coarse)').matches) return;
      this.isPaused = false;
      this.classList.remove('is-paused');
      this.restartAutoplay();
    });

    // Pause when focused inside (keyboard nav)
    this.addEventListener('focusin', () => {
      this.isPaused = true;
      this.classList.add('is-paused');
      this.stopAutoplay();
    });

    this.addEventListener('focusout', (event) => {
      // Resume only when focus has fully left the strip
      if (!this.contains(event.relatedTarget)) {
        this.isPaused = false;
        this.classList.remove('is-paused');
        this.restartAutoplay();
      }
    });
  }

  /* ─────────────────────────────────────────────────────────
     Tab visibility
  ───────────────────────────────────────────────────────── */

  onVisibilityChange() {
    this.isVisible = !document.hidden;
    if (this.isVisible) {
      this.classList.remove('is-paused');
      this.restartAutoplay();
    } else {
      this.classList.add('is-paused');
      this.stopAutoplay();
    }
  }

  /* ─────────────────────────────────────────────────────────
     Indicator click navigation
  ───────────────────────────────────────────────────────── */

  setupIndicators() {
    this.indicators.forEach((ind) => {
      ind.addEventListener('click', () => {
        const index = parseInt(ind.dataset.index, 10);
        if (Number.isNaN(index)) return;
        if (!this.isCurrentlyFade()) return;

        this.activate(index);

        // User interaction — restart the cycle from now
        this.stopAutoplay();
        this.restartAutoplay();
      });
    });
  }
}

if (!customElements.get('ff-trust-strip')) {
  customElements.define('ff-trust-strip', FFTrustStrip);
}